import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import CasinoGameHeader from '@swf/components/CasinoGameHeader/CasinoGameHeader';
import CasinoGameList from '@swf/components/CasinoGameList/CasinoGameList';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { gameEntity } from '@swf/core/analytics';
import { Header } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import type { WidgetFC, WidgetInstanceProps } from '@swf/core/types/Widget';
import { mosaicLimiter } from '@swf/core/utils/casino';
import type { CasinoIcon } from '@swf/generated/types/common/Casino';
import type {
  CasinoPlayerGamesWidgetConfig,
  CasinoPlayerGamesWidgetServices
} from '@swf/generated/types/widgets/CasinoPlayerGamesWidget';

import styles from './CasinoPlayerGamesWidget.scss';

interface RapidStreamData extends StreamData<CasinoGame> {
  title?: string;
}
export interface Data {
  games?: RapidStreamData;
}

type Props = WidgetInstanceProps<
  CasinoPlayerGamesWidgetConfig,
  CasinoPlayerGamesWidgetServices,
  Data
>;

const CasinoPlayerGamesWidget: WidgetFC<Props> = ({ data = {}, config, services, style }) => {
  const title = data?.games?.title || '';
  const games = data?.games?.games || [];
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const sliderRef = React.createRef<SliderHandles>();
  const container = React.createRef<HTMLDivElement>();
  const icon = get(services, 'games.params.ims.tags[0]', 'icnSquare') as CasinoIcon;

  const { mosaicLayout } = config;
  const [realGames, setRealGames] = useState<CasinoGame[]>([]);

  useEffect(() => {
    if (isAuthenticated) {
      if (mosaicLayout.enabled) {
        const result = mosaicLimiter(config, (i: number) => ['mosaicLayout', `section${i}`], games);
        setRealGames(result);
      } else {
        setRealGames(games);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, games, mosaicLayout.enabled]);

  return isAuthenticated && realGames.length >= config.minGames ? (
    <section className={styles.container} ref={container} style={style}>
      <Vary by={[Header.Country, Header.Currency]} />
      <CasinoGameHeader
        {...config}
        title={title}
        icon={icon}
        filters="none"
        moreLink=""
        slide
        sliderRef={sliderRef}
        sliderState={sliderState}
        stickyHeader={false}
        widgetRef={container}
      />
      <CasinoGameList
        games={realGames}
        icon={icon}
        slide
        sliderRef={sliderRef}
        onScroll={setSliderState}
        mosaic={config.mosaicLayout}
      />
    </section>
  ) : null;
};

CasinoPlayerGamesWidget.trackingReducer = (services, data) => data?.games?.games?.map(gameEntity);

export default CasinoPlayerGamesWidget;
